import { useState, Link, axios, Button, Input, Badge, Card, CardContent, Loader2, Sparkles, Check, ArrowRight, Copy, CalendarDays, Link2, Target, TrendingUp, API, toast } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

const DemoBadge = () => (
  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Demo · sample data</Badge>
);

export default function DemoPage() {
  const [topic, setTopic] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [clicks, setClicks] = useState(0);
  const [leadEmail, setLeadEmail] = useState("");
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);

  const logEvent = (label) => setEvents((e) => [{ label, at: new Date().toLocaleTimeString() }, ...e].slice(0, 8));

  const generate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setLoading(true);
    setDraft(null);
    try {
      const res = await axios.post(`${API}/content/demo`, { niche: topic });
      setDraft(res.data.post);
      logEvent("generation.completed");
    } catch {
      toast.error("Generation failed — try again");
    }
    setLoading(false);
  };

  const sampleLink = "creatara.ai/api/r/demo123";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Interactive demo — try the full Creatara loop, no signup</title>
        <meta name="description" content="Generate a real AI draft, preview scheduling, click a tracked link, submit a sample lead and watch the analytics event fire — all without an account." />
        <link rel="canonical" href="https://creatara.ai/demo" />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to="/register"><Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="demo-page-signup-btn">Sign up free</Button></Link>
        </div>
      </nav>

      <section className="pt-14 pb-8 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-amber-500/15 text-amber-400 border-amber-500/30">Interactive demo — no signup, sample data throughout</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">The whole loop, in 60 seconds</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">Topic → real AI draft → schedule preview → tracked link → lead form → analytics event. The draft is genuinely generated; everything else is a watermarked simulation.</p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Step 1 — real generation */}
          <Card className="glass" data-testid="demo-step-generate">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-500" />1 · Generate a draft (real AI)</h2>
              </div>
              <form onSubmit={generate} className="flex gap-2">
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Your topic, e.g. meal-prep for busy parents" className="flex-1 bg-white/5 border-white/15" disabled={loading} data-testid="demo-page-topic-input" />
                <Button type="submit" disabled={loading} className="bg-violet-600 hover:bg-violet-700" data-testid="demo-page-generate-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
                </Button>
              </form>
              {draft && (
                <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10 relative" data-testid="demo-page-draft">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-teal-500/15 text-teal-400 border-teal-500/30 text-[10px]">AI draft — review before publishing</Badge>
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}><Copy className="w-3 h-3 mr-1" />Copy</Button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{draft}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2 — schedule preview */}
          <Card className={`glass ${!draft ? "opacity-50 pointer-events-none" : ""}`} data-testid="demo-step-schedule">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-teal-500" />2 · Schedule preview</h2>
                <DemoBadge />
              </div>
              <p className="text-sm text-muted-foreground mb-3">In the real app this queues auto-publishing to X, LinkedIn, Bluesky and Telegram. Here, nothing is posted.</p>
              {!scheduled ? (
                <Button size="sm" onClick={() => { setScheduled(true); logEvent("post.scheduled (sample)"); }} className="bg-teal-600 hover:bg-teal-700" data-testid="demo-page-schedule-btn">
                  Schedule for tomorrow 9:00
                </Button>
              ) : (
                <p className="text-sm text-teal-400 flex items-center gap-2" data-testid="demo-page-scheduled-confirm"><Check className="w-4 h-4" />Scheduled (simulation) — tomorrow 09:00, X + LinkedIn</p>
              )}
            </CardContent>
          </Card>

          {/* Step 3 — tracked link */}
          <Card className={`glass ${!draft ? "opacity-50 pointer-events-none" : ""}`} data-testid="demo-step-link">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-500" />3 · Tracked link</h2>
                <DemoBadge />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <code className="text-xs bg-white/5 border border-white/10 rounded px-3 py-2">{sampleLink}</code>
                <Button size="sm" variant="outline" className="border-white/15" onClick={() => { setClicks((c) => c + 1); logEvent("link.clicked (sample)"); }} data-testid="demo-page-click-link-btn">
                  Simulate a click
                </Button>
                <span className="text-sm text-muted-foreground">Clicks: <strong className="text-foreground" data-testid="demo-page-click-count">{clicks}</strong> <span className="text-[10px] text-amber-400">(sample)</span></span>
              </div>
            </CardContent>
          </Card>

          {/* Step 4 — lead form */}
          <Card className={`glass ${!draft ? "opacity-50 pointer-events-none" : ""}`} data-testid="demo-step-lead">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-amber-500" />4 · Lead capture</h2>
                <DemoBadge />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (!leadEmail.trim()) return; setLeads((l) => [leadEmail, ...l]); setLeadEmail(""); logEvent("lead.captured (sample)"); }} className="flex gap-2">
                <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="sample@visitor.com" className="flex-1 bg-white/5 border-white/15" data-testid="demo-page-lead-input" />
                <Button type="submit" size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="demo-page-lead-submit">Submit</Button>
              </form>
              {leads.length > 0 && (
                <p className="text-sm text-muted-foreground mt-3" data-testid="demo-page-lead-count">{leads.length} sample lead{leads.length > 1 ? "s" : ""} captured — nothing is stored.</p>
              )}
            </CardContent>
          </Card>

          {/* Step 5 — analytics feed */}
          <Card className="glass" data-testid="demo-step-analytics">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-500" />5 · Analytics events</h2>
                <DemoBadge />
              </div>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">Events appear here as you use the demo above.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="demo-page-events-list">
                  {events.map((ev, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-center gap-2"><span className="text-purple-400 font-mono">{ev.at}</span><code>{ev.label}</code></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="text-center pt-4">
            <Link to="/register">
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="demo-page-bottom-cta">
                Do it for real — free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">{CLAIMS.freeAllowance.generations} free generations / {CLAIMS.freeAllowance.period}. No card required.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
